import { api, getApiBaseUrl } from './api';

export interface AiAssistantStatus {
  configured: boolean;
  chatModel: string;
  realtimeModel: string;
  agentFramework?: boolean;
  liveReadTools?: boolean;
}

export interface AiAssistantChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const aiAssistantApi = {
  status: async (): Promise<AiAssistantStatus> => {
    const response = await api.get<AiAssistantStatus>('/ai-assistant/status');
    return response.data;
  },

  chat: async (
    messages: AiAssistantChatMessage[],
    chatId?: number | null,
  ): Promise<{ text: string; chatId: number; title: string }> => {
    const response = await api.post<{ text: string; chatId: number; title: string }>('/ai-assistant/chat', {
      messages,
      chatId: chatId ?? undefined,
    });
    return response.data;
  },

  chats: async (): Promise<Array<{ id: number; title: string; createdAt: string; updatedAt: string }>> => {
    const response = await api.get('/ai-assistant/chats');
    return response.data;
  },

  chatThread: async (id: number): Promise<{
    id: number;
    title: string;
    messages: Array<{ id: number; role: string; content: string; createdAt: string }>;
  }> => {
    const response = await api.get(`/ai-assistant/chats/${id}`);
    return response.data;
  },

  deleteChat: async (id: number): Promise<void> => {
    await api.delete(`/ai-assistant/admin/chats/${id}`);
  },

  adminUsers: async (): Promise<Array<{ employeeId: number; name: string; chatCount: number; lastConversationAt: string }>> => {
    const response = await api.get('/ai-assistant/admin/users');
    return response.data;
  },

  adminUserChats: async (employeeId: number): Promise<Array<{ id: number; title: string; createdAt: string; updatedAt: string }>> => {
    const response = await api.get(`/ai-assistant/admin/users/${employeeId}/chats`);
    return response.data;
  },

  adminChat: async (id: number): Promise<{
    id: number;
    title: string;
    messages: Array<{ id: number; role: string; content: string; createdAt: string }>;
  }> => {
    const response = await api.get(`/ai-assistant/admin/chats/${id}`);
    return response.data;
  },

  renameChat: async (id: number, title: string): Promise<{ id: number; title: string }> => {
    const response = await api.patch<{ id: number; title: string }>(`/ai-assistant/chats/${id}`, { title });
    return response.data;
  },

  downloadChat: async (id: number, format: 'xlsx' | 'pdf'): Promise<void> => {
    const response = await api.get(`/ai-assistant/chats/${id}/export`, {
      params: { format },
      responseType: 'blob',
    });
    const disposition = String(response.headers['content-disposition'] || '');
    const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
    const filename = match?.[1] || `chat.${format}`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  appendChat: async (chatId: number | null, role: 'user' | 'assistant', content: string): Promise<{ chatId: number; title: string }> => {
    const response = await api.post<{ chatId: number; title: string }>('/ai-assistant/chats/entries', {
      chatId,
      role,
      content,
    });
    return response.data;
  },

  executeTool: async (
    name: string,
    args?: Record<string, unknown>,
  ): Promise<Record<string, unknown>> => {
    const response = await api.post('/ai-assistant/tools/execute', {
      name,
      arguments: args ? JSON.stringify(args) : undefined,
    });
    return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
  },

  createRealtimeSession: async (sdp: string): Promise<string> => {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${getApiBaseUrl()}/ai-assistant/realtime/session`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/sdp',
      },
      body: sdp,
    });
    const text = await response.text();
    if (!response.ok) {
      try {
        const err = JSON.parse(text) as { message?: string };
        throw new Error(err.message || text || `Realtime session failed (${response.status})`);
      } catch (e) {
        if (e instanceof Error && e.message !== text) throw e;
        throw new Error(text || `Realtime session failed (${response.status})`);
      }
    }
    return text;
  },
};
